import { useState } from 'react';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { siteInfo } from '@/mocks/siteInfo';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector('input[name="website_alt"]') as HTMLInputElement;
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      if (honeypotInput) formData.delete('website_alt');

      const response = await fetch('https://readdy.ai/api/form/d9qapscnlsngrm4llup0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.toLowerCase().includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后重试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteInfo.name}`,
    description: siteInfo.description,
    url: `${siteInfo.url}/about`,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    about: {
      '@type': 'Organization',
      name: siteInfo.name,
      description: siteInfo.brandIntro,
      url: siteInfo.url,
      contactPoint: {
        '@type': 'ContactPoint',
        email: siteInfo.contact.email,
        contactType: 'customer service',
      },
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title="关于我们"
        description={`了解${siteInfo.name}的品牌故事、口碑评价及联系方式。${siteInfo.description.substring(0, 80)}`}
        keywords="大桥未久,关于大桥未久,大桥未久专区,大桥未久联系"
        canonicalUrl={`${siteInfo.url}/about`}
        schemaData={aboutSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="py-12 md:py-16 px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center mb-16 md:mb-20">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-6">
                  关于我们
                </h1>
                <div className="space-y-4 text-foreground-700 leading-relaxed">
                  <p>{siteInfo.brandIntro}</p>
                </div>
              </div>
              <div className="relative">
                <div className="rounded-lg overflow-hidden h-72 md:h-80">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20minimal%20editorial%20office%20space%20with%20warm%20lighting%2C%20Japanese%20design%20aesthetic%2C%20wooden%20furniture%20and%20soft%20textiles%2C%20calm%20sophisticated%20atmosphere%2C%20natural%20light%20through%20shoji%20screens%2C%20professional%20creative%20workspace&width=800&height=600&seq=about-brand&orientation=landscape"
                    alt="大桥未久官方专区"
                    className="w-full h-full object-cover object-top rounded-lg"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-accent-500 text-white rounded-lg px-4 py-3 hidden md:block">
                  <p className="text-sm font-heading font-semibold">始于热爱</p>
                  <p className="text-xs opacity-80">用心服务每一位粉丝</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center mb-16 md:mb-20">
              <div className="order-2 lg:order-1">
                <div className="rounded-lg overflow-hidden h-72 md:h-80">
                  <img
                    src="https://readdy.ai/api/search-image?query=Premium%20quality%20award%20certificate%20display%20on%20elegant%20wall%2C%20warm%20golden%20accent%20lighting%2C%20sophisticated%20interior%2C%20professional%20achievement%20atmosphere%2C%20deep%20red%20and%20cream%20tones%2C%20editorial%20photography&width=800&height=600&seq=about-reputation&orientation=landscape"
                    alt="口碑评价"
                    className="w-full h-full object-cover object-top rounded-lg"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  口碑与认可
                </h2>
                <div className="space-y-4 text-foreground-700 leading-relaxed">
                  <p>{siteInfo.reputation}</p>
                </div>
                <div className="grid grid-cols-3 gap-4 mt-8">
                  <div className="text-center p-4 bg-background-100 rounded-lg border border-background-200/70">
                    <div className="text-lg font-heading font-bold text-accent-500">98%</div>
                    <div className="text-xs text-foreground-500 mt-1">满意度</div>
                  </div>
                  <div className="text-center p-4 bg-background-100 rounded-lg border border-background-200/70">
                    <div className="text-lg font-heading font-bold text-accent-500">4.9</div>
                    <div className="text-xs text-foreground-500 mt-1">综合评分</div>
                  </div>
                  <div className="text-center p-4 bg-background-100 rounded-lg border border-background-200/70">
                    <div className="text-lg font-heading font-bold text-accent-500">10年+</div>
                    <div className="text-xs text-foreground-500 mt-1">运营时间</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  联系我们
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-mail-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">邮箱</p>
                      <a
                        href={`mailto:${siteInfo.contact.email}`}
                        className="text-sm text-foreground-600 hover:text-primary-500 transition-colors"
                      >
                        {siteInfo.contact.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-weibo-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">微博</p>
                      <p className="text-sm text-foreground-600">{siteInfo.contact.weibo}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-wechat-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">微信</p>
                      <p className="text-sm text-foreground-600">{siteInfo.contact.wechat}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/70">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">
                    发送消息
                  </h3>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form=""
                    className="space-y-4"
                    noValidate
                  >
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1">
                        您的姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1">
                        电子邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="请输入您的电子邮箱"
                      />
                    </div>

                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1">
                        主题
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="请输入消息主题"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                        placeholder="请输入您的留言（500字以内）"
                      ></textarea>
                      <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                    </div>

                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="absolute"
                      style={{ left: '-9999px', opacity: 0, height: 0, width: 0, overflow: 'hidden', pointerEvents: 'none' }}
                    />

                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white px-6 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'submitting' ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i> 发送中...
                        </span>
                      ) : (
                        '发送消息'
                      )}
                    </button>

                    {formStatus === 'success' && (
                      <div className="p-3 bg-accent-100 text-accent-800 rounded-lg text-sm flex items-start gap-2">
                        <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                        <span>{formMessage}</span>
                      </div>
                    )}

                    {formStatus === 'error' && (
                      <div className="p-3 bg-primary-100 text-primary-800 rounded-lg text-sm flex items-start gap-2">
                        <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i>
                        <span>{formMessage}</span>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}